import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canAccessEssPortal } from './utils/roles';
import AppLayout from './components/layout/AppLayout';
import Spinner from './components/common/Spinner';
import './styles/global.css';

// Resilient lazy loader that auto-retries or reloads once if a chunk fails to load (prevents white screen)
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('mi_hrm_chunk_retry') || 'false'
    );
    try {
      const component = await componentImport();
      window.sessionStorage.setItem('mi_hrm_chunk_retry', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('mi_hrm_chunk_retry', 'true');
        window.location.reload();
        return { default: () => <Spinner fullscreen /> };
      }
      throw error;
    }
  });

// Lazy-loaded pages
const Login       = lazyWithRetry(() => import('./pages/Login'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const Setup       = lazyWithRetry(() => import('./pages/Setup'));
const Dashboard   = lazyWithRetry(() => import('./pages/Dashboard'));
const Employees      = lazyWithRetry(() => import('./pages/Employees'));
const EmployeeProfile = lazyWithRetry(() => import('./pages/EmployeeProfile'));
const Announcements   = lazyWithRetry(() => import('./pages/Announcements'));
const Departments    = lazyWithRetry(() => import('./pages/Departments'));
const Leave       = lazyWithRetry(() => import('./pages/Leave'));
const MedicalPermissions = lazyWithRetry(() => import('./pages/MedicalPermissions'));
const GatePass       = lazyWithRetry(() => import('./pages/GatePass'));
const Memos          = lazyWithRetry(() => import('./pages/Memos'));
const Attendance  = lazyWithRetry(() => import('./pages/Attendance'));
const Payroll     = lazyWithRetry(() => import('./pages/Payroll'));
const LoansAdvances  = lazyWithRetry(() => import('./pages/LoansAdvances'));
const Payslips    = lazyWithRetry(() => import('./pages/Payslips'));
const Performance = lazyWithRetry(() => import('./pages/Performance'));
const Recruitment = lazyWithRetry(() => import('./pages/Recruitment'));
const Training    = lazyWithRetry(() => import('./pages/Training'));
const Grievances  = lazyWithRetry(() => import('./pages/Grievances'));
const Audit       = lazyWithRetry(() => import('./pages/Audit'));
const Users       = lazyWithRetry(() => import('./pages/Users'));
const System      = lazyWithRetry(() => import('./pages/System'));
const Reports     = lazyWithRetry(() => import('./pages/Reports'));
const ExitRequest = lazyWithRetry(() => import('./pages/ExitRequest'));
const NotFound    = lazyWithRetry(() => import('./pages/NotFound'));

function ProtectedRoute({ children, module, vkey, action = 'view' }) {
  const { user, loading, can, logout } = useAuth();
  // Single loading state — show spinner only once at the top level
  if (loading) return <Spinner fullscreen />;
  if (!user)   return <Navigate to="/login" replace />;

  if (!canAccessEssPortal(user)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', padding: 24 }}>
        <div style={{ maxWidth: 460, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 28, textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 700, color: '#0F172A' }}>ESS Portal Access Disabled</h2>
          <p style={{ margin: '0 0 20px 0', fontSize: 13.5, color: '#64748B' }}>
            Your current security role does not have ESS Portal access enabled. Please contact your System Administrator.
          </p>
          <button className="btn btn-primary" onClick={logout}>Sign Out</button>
        </div>
      </div>
    );
  }

  // If a granular vkey is specified and explicitly configured in user.permissions, enforce it
  if (vkey && Array.isArray(user.permissions) && user.permissions.some(p => p.module === vkey)) {
    if (!can(vkey, action)) return <Navigate to="/dashboard" replace />;
    return children;
  }

  // Module permission check (only if a module is specified)
  if (module && !can(module, action)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner fullscreen />;
  if (user)    return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<Spinner fullscreen />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Protected — inside app layout (no double-wrap; each child handles its own permissions) */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard"     element={<ProtectedRoute vkey="ess_dashboard"><Dashboard /></ProtectedRoute>} />
          <Route path="/announcements" element={<ProtectedRoute vkey="ess_announcements"><Announcements /></ProtectedRoute>} />
          <Route path="/profile"       element={<ProtectedRoute vkey="ess_profile"><EmployeeProfile /></ProtectedRoute>} />
          <Route path="/employees"     element={<Navigate to="/dashboard" replace />} />
          <Route path="/employees/:id" element={<Navigate to="/dashboard" replace />} />
          <Route path="/departments"   element={<ProtectedRoute module="org_chart" vkey="ess_departments"><Departments /></ProtectedRoute>} />
          <Route path="/leave"         element={<ProtectedRoute module="leave_management" vkey="ess_leave"><Leave /></ProtectedRoute>} />
          <Route path="/medical"       element={<ProtectedRoute module="leave_management" vkey="ess_medical"><MedicalPermissions /></ProtectedRoute>} />
          <Route path="/gatepass"      element={<ProtectedRoute module="attendance_tracker" vkey="ess_gatepass"><GatePass /></ProtectedRoute>} />
          <Route path="/memos"         element={<ProtectedRoute module="employee_self_svc" vkey="ess_memos"><Memos /></ProtectedRoute>} />
          <Route path="/attendance"    element={<ProtectedRoute module="attendance_tracker" vkey="ess_attendance"><Attendance /></ProtectedRoute>} />
          <Route path="/payroll"       element={<Navigate to="/dashboard" replace />} />
          <Route path="/loans"         element={<ProtectedRoute module="employee_self_svc" vkey="ess_loans"><LoansAdvances /></ProtectedRoute>} />
          <Route path="/payslips"      element={<ProtectedRoute module="payslip_generator" vkey="ess_payslips"><Payslips /></ProtectedRoute>} />
          <Route path="/performance"   element={<ProtectedRoute module="performance_reviews" vkey="ess_performance"><Performance /></ProtectedRoute>} />
          <Route path="/recruitment"   element={<ProtectedRoute module="recruitment_portal" vkey="ess_recruitment"><Recruitment /></ProtectedRoute>} />
          <Route path="/training"      element={<ProtectedRoute module="training_certs" vkey="ess_training"><Training /></ProtectedRoute>} />
          <Route path="/grievances"    element={<ProtectedRoute module="grievance_mgmt" vkey="ess_grievances"><Grievances /></ProtectedRoute>} />
          <Route path="/audit"         element={<ProtectedRoute module="audit_logs"><Audit /></ProtectedRoute>} />
          <Route path="/users"         element={<ProtectedRoute module="user_management"><Users /></ProtectedRoute>} />
          <Route path="/system"        element={<ProtectedRoute module="system_config"><System /></ProtectedRoute>} />
          <Route path="/reports"       element={<Navigate to="/dashboard" replace />} />
          <Route path="/exit-request"  element={<ProtectedRoute vkey="ess_exit_request"><ExitRequest /></ProtectedRoute>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ maxWidth: 500, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 32, textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
            <h3 style={{ color: '#DC2626', margin: '0 0 10px 0', fontSize: 18 }}>Something went wrong loading this page</h3>
            <p style={{ color: '#64748B', fontSize: 13.5, margin: '0 0 20px 0' }}>
              {this.state.error?.message || 'An unexpected rendering error occurred.'}
            </p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: "'Outfit', sans-serif", fontSize: '13.5px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
            success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#DC2626', secondary: '#fff' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
